import React, { useState } from 'react';
import { Code, Copy, Check, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  authentication: boolean;
  parameters?: {
    name: string;
    type: string;
    required: boolean;
    description: string;
  }[];
  response: {
    success: any;
    error: any;
  };
}

const API_ENDPOINTS: Endpoint[] = [
  {
    method: 'GET',
    path: '/api/alunos',
    description: 'Lista todos os alunos cadastrados',
    authentication: true,
    parameters: [
      {
        name: 'page',
        type: 'number',
        required: false,
        description: 'Número da página (padrão: 1)'
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Itens por página (padrão: 20)'
      },
      {
        name: 'search',
        type: 'string',
        required: false,
        description: 'Busca por nome, email ou documento'
      }
    ],
    response: {
      success: {
        data: [
          {
            id: 'uuid',
            nome: 'string',
            email: 'string',
            documento: 'string',
            cursos: ['string'],
            ultimo_acesso: 'date'
          }
        ],
        total: 'number',
        page: 'number',
        totalPages: 'number'
      },
      error: {
        message: 'string',
        code: 'string'
      }
    }
  },
  {
    method: 'GET',
    path: '/api/metricas',
    description: 'Retorna métricas gerais do sistema',
    authentication: true,
    response: {
      success: {
        total_alunos: 'number',
        alunos_ativos: 'number',
        total_cursos: 'number',
        taxa_engajamento: 'number',
        emails_enviados: 'number',
        taxa_abertura: 'number'
      },
      error: {
        message: 'string',
        code: 'string'
      }
    }
  },
  {
    method: 'GET',
    path: '/api/email-engagements',
    description: 'Retorna dados de engajamento por email',
    authentication: true,
    parameters: [
      {
        name: 'start_date',
        type: 'date',
        required: false,
        description: 'Data inicial (YYYY-MM-DD)'
      },
      {
        name: 'end_date',
        type: 'date',
        required: false,
        description: 'Data final (YYYY-MM-DD)'
      }
    ],
    response: {
      success: {
        data: [
          {
            date: 'date',
            total_enviados: 'number',
            total_abertos: 'number',
            taxa_abertura: 'number',
            clicks: 'number'
          }
        ]
      },
      error: {
        message: 'string',
        code: 'string'
      }
    }
  },
  {
    method: 'POST',
    path: '/api/webhooks',
    description: 'Cria um novo webhook',
    authentication: true,
    parameters: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Nome do webhook'
      },
      {
        name: 'url',
        type: 'string',
        required: true,
        description: 'URL que receberá as requisições'
      },
      {
        name: 'events',
        type: 'string[]',
        required: true,
        description: 'Lista de eventos para monitorar'
      }
    ],
    response: {
      success: {
        id: 'string',
        name: 'string',
        url: 'string',
        events: ['string'],
        secret: 'string',
        created_at: 'date'
      },
      error: {
        message: 'string',
        code: 'string'
      }
    }
  }
];

export function ApiDocumentation() {
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);

  const copyToClipboard = (text: string, endpoint: string) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpoint);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-green-500/10 text-green-400';
      case 'POST': return 'bg-blue-500/10 text-blue-400';
      case 'PUT': return 'bg-orange-500/10 text-orange-400';
      case 'DELETE': return 'bg-red-500/10 text-red-400';
      default: return 'bg-gray-500/10 text-gray-400';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Documentação da API</h2>
          <p className="text-sm text-gray-400">Explore e teste os endpoints disponíveis</p>
        </div>
        <a
          href="https://documenter.getpostman.com/view/neurosaber-api"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-[#268c73] hover:text-[#1a7a61] transition-colors"
        >
          Ver no Postman
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Autenticação */}
      <div className="bg-background-card p-6 rounded-lg mb-6">
        <h3 className="text-lg font-medium text-white mb-4">Autenticação</h3>
        <p className="text-gray-400 mb-4">
          Todas as requisições autenticadas devem incluir o token JWT no header Authorization:
        </p>
        <div className="bg-background p-4 rounded-lg font-mono text-sm text-gray-300">
          <div className="flex items-center justify-between">
            <code>Authorization: Bearer {'<seu-token-jwt>'}</code>
            <button
              onClick={() => copyToClipboard('Authorization: Bearer <seu-token-jwt>', 'auth')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              {copiedEndpoint === 'auth' ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Endpoints */}
      <div className="space-y-4">
        {API_ENDPOINTS.map((endpoint) => (
          <div key={endpoint.path} className="bg-background-card rounded-lg">
            <div
              className="p-6 cursor-pointer"
              onClick={() => setExpandedEndpoint(
                expandedEndpoint === endpoint.path ? null : endpoint.path
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-lg font-mono text-sm ${getMethodColor(endpoint.method)}`}>
                    {endpoint.method}
                  </span>
                  <code className="text-white font-mono">{endpoint.path}</code>
                </div>
                {expandedEndpoint === endpoint.path ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <p className="text-gray-400 mt-2">{endpoint.description}</p>
            </div>

            {expandedEndpoint === endpoint.path && (
              <div className="px-6 pb-6 border-t border-border pt-6">
                {/* Parâmetros */}
                {endpoint.parameters && endpoint.parameters.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Parâmetros</h4>
                    <div className="bg-background rounded-lg overflow-hidden">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="px-4 py-2 text-sm font-medium text-gray-400">Nome</th>
                            <th className="px-4 py-2 text-sm font-medium text-gray-400">Tipo</th>
                            <th className="px-4 py-2 text-sm font-medium text-gray-400">Obrigatório</th>
                            <th className="px-4 py-2 text-sm font-medium text-gray-400">Descrição</th>
                          </tr>
                        </thead>
                        <tbody>
                          {endpoint.parameters.map((param) => (
                            <tr key={param.name} className="border-b border-border last:border-0">
                              <td className="px-4 py-2 text-sm font-mono text-white">{param.name}</td>
                              <td className="px-4 py-2 text-sm text-blue-400">{param.type}</td>
                              <td className="px-4 py-2 text-sm">
                                {param.required ? (
                                  <span className="text-red-400">Sim</span>
                                ) : (
                                  <span className="text-gray-400">Não</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-300">{param.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Exemplo de Resposta */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Resposta</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-green-400 mb-2">Sucesso (200)</p>
                      <div className="relative bg-background rounded-lg p-4">
                        <pre className="text-sm text-gray-300 font-mono">
                          {JSON.stringify(endpoint.response.success, null, 2)}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(
                            JSON.stringify(endpoint.response.success, null, 2),
                            `${endpoint.path}-success`
                          )}
                          className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
                        >
                          {copiedEndpoint === `${endpoint.path}-success` ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-red-400 mb-2">Erro (4xx/5xx)</p>
                      <div className="relative bg-background rounded-lg p-4">
                        <pre className="text-sm text-gray-300 font-mono">
                          {JSON.stringify(endpoint.response.error, null, 2)}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(
                            JSON.stringify(endpoint.response.error, null, 2),
                            `${endpoint.path}-error`
                          )}
                          className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
                        >
                          {copiedEndpoint === `${endpoint.path}-error` ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exemplo de Código */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Exemplo de Código</h4>
                  <div className="bg-background rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Code className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-400">JavaScript/TypeScript</span>
                    </div>
                    <pre className="text-sm text-gray-300 font-mono">
{`const response = await fetch('${endpoint.path}', {
  method: '${endpoint.method}',
  headers: {
    'Authorization': 'Bearer <seu-token-jwt>',
    'Content-Type': 'application/json'
  }${endpoint.method !== 'GET' ? `,
  body: JSON.stringify({
    // ... dados da requisição
  })` : ''}
});

const data = await response.json();`}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}